import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Wand2, Mic, Image as ImageIcon, Clapperboard, ArrowRight,
  Zap, Brain, Download, Layers, Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SHOWCASE } from "../lib/assets";

const Step = ({ n, icon: Icon, title, desc }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: n * 0.08 }}
    className="relative glass rounded-2xl p-7"
  >
    <span className="font-code text-xs text-[#FF6A5E]">0{n}</span>
    <div className="mt-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF453A]/12 border border-[#FF453A]/25">
      <Icon className="h-6 w-6 text-[#FF6A5E]" />
    </div>
    <h3 className="mt-5 font-display font-bold text-xl">{title}</h3>
    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
  </motion.div>
);

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="relative overflow-hidden">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#FF453A]/18 blur-[130px]" />
      <div className="pointer-events-none absolute top-96 -left-40 h-[440px] w-[440px] rounded-full bg-[#3B82F6]/12 blur-[130px]" />

      {/* HERO */}
      <section className="relative grid-lines">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <div className="animate-fadeup">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[#FF6A5E]" />
              <span className="eyebrow">AI faceless ad generator</span>
            </div>
            <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              Turn a product<br />
              into a <span className="shimmer-text">scroll-stopping</span><br />
              video ad.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              Describe what you sell. Our AI writes the script, generates the visuals,
              voices it over, and stitches a ready-to-post 9:16 video — in minutes. No camera, no editing, no face required.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-create-btn"
                onClick={() => navigate("/studio")}
                className="h-13 bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-7 py-6 text-base font-semibold transition-colors glow-coral"
              >
                Create your first ad <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link
                to="/library"
                data-testid="hero-library-btn"
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                <Play className="h-4 w-4" /> Browse the gallery
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Brain className="h-4 w-4 text-[#FF6A5E]" /> Claude · GPT · Gemini</span>
              <span className="flex items-center gap-2"><Mic className="h-4 w-4 text-[#FF6A5E]" /> Studio voiceover</span>
            </div>
          </div>

          {/* phone mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 3 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="phone-frame w-[290px] sm:w-[320px] animate-floaty"
            >
              <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[30px] bg-black">
                <img src={SHOWCASE[1]} alt="ad preview" className="absolute inset-0 h-full w-full object-cover kenburns" style={{ animationDuration: "12s", animationIterationCount: "infinite", animationDirection: "alternate" }} />
                <div className="absolute top-3 left-3 right-3 flex gap-1.5">
                  {[100, 60, 0].map((w) => (
                    <span key={w} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
                      <span className="block h-full bg-white rounded-full" style={{ width: `${w}%` }} />
                    </span>
                  ))}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5 pb-8 bg-gradient-to-t from-black via-black/70 to-transparent">
                  <div className="w-9 h-1.5 bg-[#FF453A] rounded mb-2.5" />
                  <p className="font-display font-extrabold text-white text-2xl leading-tight uppercase text-glow">
                    Your best<br />seller, sold out.
                  </p>
                </div>
              </div>
            </motion.div>
            {/* floating audio chip */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="hidden sm:flex absolute -left-4 bottom-16 glass rounded-2xl px-4 py-3 items-center gap-3"
            >
              <Mic className="h-4 w-4 text-[#FF6A5E]" />
              <div className="flex items-end gap-1 h-6">
                {[0.2, 0.5, 0.9, 0.4, 0.7, 0.3].map((d) => (
                  <span key={d} className="wave-bar w-1 bg-[#FF6A5E] rounded" style={{ height: "100%", animationDelay: `${d}s` }} />
                ))}
              </div>
              <span className="text-xs text-slate-300 font-medium">Nova voice</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="max-w-2xl">
          <span className="eyebrow">The pipeline</span>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
            Four inputs. One finished ad.
          </h2>
          <p className="mt-3 text-slate-400">Built for non-technical founders. If you can describe your product, you can make the video.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Step n={1} icon={Wand2} title="Describe it" desc="Drop your product name, the hook, and who it's for. Or start from a template." />
          <Step n={2} icon={Brain} title="AI scripts it" desc="Claude, GPT or Gemini writes a punchy 5-beat script tuned to your chosen tone." />
          <Step n={3} icon={ImageIcon} title="Visuals + voice" desc="AI images or stock photos per scene, narrated by a studio OpenAI voice." />
          <Step n={4} icon={Download} title="Render & post" desc="We stitch kinetic captions into a 9:16 MP4 you can download and post anywhere." />
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-center">
          <div>
            <span className="eyebrow">Any niche, any product</span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
              Ads that look like a studio made them.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              From luxury skincare to SaaS tools, every ad gets a distinct visual language, animated captions,
              and a natural voiceover — automatically composed for vertical feeds.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: Zap, label: "15–60s formats" },
                { icon: Layers, label: "Editable scenes" },
                { icon: Mic, label: "8 studio voices" },
                { icon: Clapperboard, label: "1080×1920 MP4" },
              ].map((f) => (
                <div key={f.label} className="flex items-center gap-3 text-sm text-slate-300">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF453A]/12 border border-[#FF453A]/20">
                    <f.icon className="h-4 w-4 text-[#FF6A5E]" />
                  </span>
                  {f.label}
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {SHOWCASE.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative overflow-hidden rounded-2xl border border-white/10 ${i === 1 ? "translate-y-6" : ""}`}
              >
                <div className="aspect-[9/16]">
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <div className="w-6 h-1 bg-[#FF453A] rounded mb-1.5" />
                  <p className="font-display font-bold text-white text-xs uppercase">Scene {i + 1}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="relative overflow-hidden rounded-3xl glass p-10 sm:p-16 text-center grain">
          <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-64 w-[600px] rounded-full bg-[#FF453A]/25 blur-[120px]" />
          <h2 className="relative font-display font-extrabold text-4xl sm:text-5xl tracking-tight">
            Your FREE viral ad is one prompt away.
          </h2>
          <p className="relative mt-4 text-slate-300 max-w-xl mx-auto">
            No credit card. No login. Just describe your product and hit generate.
          </p>
          <Button
            data-testid="cta-create-btn"
            onClick={() => navigate("/studio")}
            className="relative mt-8 bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-8 py-6 text-base font-semibold transition-colors glow-coral"
          >
            Open the studio <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        <p>© {new Date().getFullYear()} Faceless Studio — build ads without showing your face.</p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-slate-400 hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <span className="text-white/20">·</span>
          <Link to="/terms" data-testid="footer-terms-link" className="text-slate-400 hover:text-white transition-colors">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}
